import { useLocale } from '@/components/Link'
import { localeDate } from '@/components/page-specific/catering/common/helpers/date'
import { useDeliveryTimeLabel } from '@/components/page-specific/catering/common/helpers/useDeliveryTimeLabel'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Avatar, Box, Stack, Typography } from '@mui/material'

import { parseISO } from 'date-fns'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

// The basket card that follows slides over this much of the banner, so the two read as one
// stacked object. Matches the basket's 20px corner radius.
const BASKET_OVERLAP = 20

interface CateringTeamOrderContributorBannerProps {
  ordererName: string
  deliveryDateIso: string | null
  deliveryTime: string | null
  locationLabel: string
  /** Reports the visible height (overlap excluded) so the basket can size to the remaining viewport. */
  onHeightChange?: (height: number) => void
}

interface DetailRowProps {
  icon: React.ReactNode
  label: string
  value: string
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0} flex={1}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.100', color: 'text.primary' }}>
        <Box sx={{ display: 'flex', '& svg': { fontSize: 14 } }}>{icon}</Box>
      </Avatar>
      <Stack minWidth={0}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.5px', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
      </Stack>
    </Stack>
  )
}

export function CateringTeamOrderContributorBanner({
  ordererName,
  deliveryDateIso,
  deliveryTime,
  locationLabel,
  onHeightChange,
}: CateringTeamOrderContributorBannerProps) {
  const { t } = useTranslation()
  const locale = useLocale()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!onHeightChange) return
    const updateHeight = () => {
      if (ref.current) onHeightChange(ref.current.offsetHeight - BASKET_OVERLAP)
    }
    const observer = new ResizeObserver(updateHeight)
    if (ref.current) observer.observe(ref.current)
    return () => {
      observer.disconnect()
      onHeightChange(0)
    }
  }, [onHeightChange])

  const dateLabel = deliveryDateIso
    ? localeDate(parseISO(deliveryDateIso), 'do MMM yyyy', locale)
    : ''
  const timeLabel = useDeliveryTimeLabel(deliveryTime ?? undefined) ?? ''
  const name = ordererName || t('marketplace:cateringTeamOrderOwnerFallback')
  const hasDetails = Boolean(dateLabel || timeLabel || locationLabel)

  return (
    <Box
      ref={ref}
      data-cy="cateringTeamOrderContributorBanner"
      sx={{
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'divider',
        // Square bottom so the basket's rounded top corners sit on grey, not on the page.
        borderRadius: `${BASKET_OVERLAP}px ${BASKET_OVERLAP}px 0 0`,
        padding: '16px 14px',
        paddingBottom: `${14 + BASKET_OVERLAP}px`,
        marginBottom: `-${BASKET_OVERLAP}px`,
      }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            fontSize: 18,
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}>
          🎉
        </Avatar>
        <Stack spacing={0.25} minWidth={0}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
            {t('marketplace:cateringTeamOrderInviteTitle', { name })}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.4 }}>
            {t('marketplace:cateringTeamOrderInviteBody')}
          </Typography>
        </Stack>
      </Stack>
      {hasDetails && (
        <Stack
          spacing={1}
          sx={{
            marginTop: 1.25,
            padding: 1.25,
            borderRadius: '12px',
            bgcolor: 'background.paper',
          }}>
          {(dateLabel || timeLabel) && (
            <Stack direction="row" spacing={2} data-cy="cateringTeamOrderBannerDateTime">
              {dateLabel && (
                <DetailRow
                  icon={<CalendarTodayIcon />}
                  label={t('marketplace:date')}
                  value={dateLabel}
                />
              )}
              {timeLabel && (
                <DetailRow
                  icon={<ScheduleIcon />}
                  label={t('marketplace:time')}
                  value={timeLabel}
                />
              )}
            </Stack>
          )}
          {locationLabel && (
            <DetailRow
              icon={<LocationOnIcon />}
              label={t('marketplace:deliverTo')}
              value={locationLabel}
            />
          )}
        </Stack>
      )}
    </Box>
  )
}
